import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from 'lib/utils';

/** A figure inside the monitor card: label over value, as on the Monitoring page. */
function Stat({ value, label, tone }: { value: string; label: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-white/10 px-3 py-2.5">
      <div className="text-[12px] text-white/55">{label}</div>
      <div className={cn('mt-1 text-xl font-bold leading-none tabular-nums text-white', tone)}>{value}</div>
    </div>
  );
}

// Decorative bar heights for the stylized submissions chart (no real data — the
// DS imagery rule says show what the product does, not a screenshot).
const BARS = [40, 55, 34, 70, 50, 88, 78];

/** Decorative panel beside the auth form: solid navy, no glows. Always dark, regardless of theme. */
export function AuthProofPanel({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn('relative hidden flex-col justify-center bg-[#0A1628] px-12 py-14 text-white', className)}>
      <div className="max-w-md">
        <h2 className="text-3xl font-bold leading-[1.15] tracking-[-0.02em]">{t('auth.proof_headline')}</h2>
        <p className="mt-4 text-[15px] leading-relaxed text-white/70">{t('auth.proof_sub')}</p>

        {/* Stylized live-monitor card */}
        <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <p className="mb-3 text-[13px] font-medium text-white/70">{t('auth.proof_monitor_title')}</p>
          <div className="grid grid-cols-3 gap-2">
            <Stat value="58" label={t('auth.proof_in_progress')} />
            <Stat value="41" label={t('auth.proof_submitted')} />
            <Stat value="5" label={t('auth.proof_flagged')} tone="text-warning-400" />
          </div>
          <div className="mt-4 flex h-16 items-end gap-1.5" aria-hidden>
            {BARS.map((h, i) => (
              <div
                key={i}
                className={cn('flex-1 rounded-sm', i === BARS.length - 2 ? 'bg-white' : 'bg-white/25')}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-2 text-sm text-white/80">
          <Check className="h-4 w-4 shrink-0 text-success-400" aria-hidden />
          {t('auth.proof_trust')}
        </div>
      </div>
    </div>
  );
}

export default AuthProofPanel;
